// ---------------------------------------------------------------------------
// The Hive between wars: placing lots and structures, Hive Level, lot yield
// ---------------------------------------------------------------------------
//
// The Hive is the war's 315-cell hex field (subplotGrid / hexGrid). Lots sit
// one per cell; each structure type has a fixed shape (axial hex offsets from
// its anchor, config `shape`) placed at an anchor cell in one of 6 rotations.
//
// Placement rules (checkHive):
//   - only owned structures, and at most the arena's max structures (and the
//     hard cap; a generated Hive, `generated: true`, only the hard cap); one
//     of each type is implied by keying placements by name
//   - a footprint must lie wholly on the field and overlap no other structure
//   - every cell of an extractor sits on a lot of its type; other structures
//     go on any cell, with or without a lot
//   - no more lots of a type on the Hive than the player owns
//
// Lot yield (hiveYield) is the war's own rule, subplotEconomy.lotYield.

import { SUBPLOT_COUNT } from '../subplotGrid.js'
import { cellsAtOffsets } from '../hexGrid.js'
import { lotNetworks, lotYield } from '../subplotEconomy.js'
import { DEFAULT_PROGRESSION, structureNames } from './config.js'
import { clonePlayer, ownsStructure, ownedLots, unplacedLots } from './player.js'

export const ROTATIONS = 6

// Rotates an axial offset by `rotation` steps of 60° clockwise.
export function rotateOffset([dq, dr], rotation) {
    let q = dq, r = dr
    for (let i = 0; i < ((rotation % 6) + 6) % 6; i++) [q, r] = [-r, q + r]
    return [q, r]
}

// The cells a structure covers at `cell` and `rotation`, or null when any of
// them would fall off the field.
export function footprintAt(name, cell, rotation, p = DEFAULT_PROGRESSION) {
    const shape = p.structures[name]?.shape
    if (!shape) throw new Error(`Unknown structure "${name}"`)
    if (!Number.isInteger(rotation) || rotation < 0 || rotation >= ROTATIONS) throw new Error(`Rotation must be 0–5, got ${rotation}`)
    if (!Number.isInteger(cell) || cell < 0 || cell >= SUBPLOT_COUNT) return null
    const offsets = shape.map(o => rotateOffset(o, rotation))
    const cells = cellsAtOffsets(cell, offsets)
    return cells.length === offsets.length ? cells : null
}

// Every placed structure's footprint: { [name]: cells }.
export function placedFootprints(player, p = DEFAULT_PROGRESSION) {
    return Object.fromEntries(Object.entries(player.hive.structures).map(([name, at]) => [name, footprintAt(name, at.cell, at.rotation, p)]))
}

// Everything wrong with the player's Hive, as readable strings ([] = valid).
export function checkHive(player, p = DEFAULT_PROGRESSION) {
    const problems = []
    const { lots, structures } = player.hive

    const placedByType = {}
    for (const [cellKey, type] of Object.entries(lots)) {
        const cell = Number(cellKey)
        if (!Number.isInteger(cell) || cell < 0 || cell >= SUBPLOT_COUNT) problems.push(`Lot on cell ${cellKey}, which is off the Hive`)
        if (!p.lotTypes[type]) problems.push(`Unknown lot type "${type}" on cell ${cellKey}`)
        placedByType[type] = (placedByType[type] ?? 0) + 1
    }
    for (const [type, n] of Object.entries(placedByType)) {
        if (n > ownedLots(player, type)) problems.push(`${n} ${type} lots placed, ${ownedLots(player, type)} owned`)
    }

    const names = Object.keys(structures)
    const cap = player.generated ? p.inputs.maxStructuresHardCap : Math.min(p.arenas[player.arena].maxStructures, p.inputs.maxStructuresHardCap)
    if (names.length > cap) problems.push(`${names.length} structures placed; arena ${player.arena} allows ${cap}`)
    const taken = new Map()
    for (const name of names) {
        if (!p.structures[name]) { problems.push(`Unknown structure "${name}"`); continue }
        if (!ownsStructure(player, name)) problems.push(`${name} is placed but not owned`)
        const { cell, rotation } = structures[name]
        let fp
        try { fp = footprintAt(name, cell, rotation, p) } catch (e) { problems.push(`${name}: ${e.message}`); continue }
        if (!fp) { problems.push(`${name} doesn't fit on the Hive at cell ${cell}, rotation ${rotation}`); continue }
        for (const c of fp) {
            if (taken.has(c)) problems.push(`${name} overlaps ${taken.get(c)} at cell ${c}`)
            else taken.set(c, name)
        }
        const lotType = p.structures[name].extractorFor
        if (lotType && fp.some(c => lots[c] !== lotType)) problems.push(`${name} must sit wholly on ${lotType} lots`)
    }
    return problems
}

function assertHive(player, p) {
    const problems = checkHive(player, p)
    if (problems.length) throw new Error(problems.join('; '))
    return player
}

// ── Edits (each returns a new player, or throws when the result is invalid) ──

export function placeLot(player, cell, type, p = DEFAULT_PROGRESSION) {
    if (player.hive.lots[cell] != null) throw new Error(`Cell ${cell} already holds a lot`)
    const out = clonePlayer(player)
    out.hive.lots[cell] = type
    return assertHive(out, p)
}

export function removeLot(player, cell, p = DEFAULT_PROGRESSION) {
    if (player.hive.lots[cell] == null) throw new Error(`Cell ${cell} holds no lot`)
    const out = clonePlayer(player)
    delete out.hive.lots[cell]
    return assertHive(out, p)
}

// Places a structure, or moves/rotates it if it's already on the Hive.
export function placeStructure(player, name, cell, rotation = 0, p = DEFAULT_PROGRESSION) {
    const out = clonePlayer(player)
    out.hive.structures[name] = { cell, rotation }
    return assertHive(out, p)
}

export function removeStructure(player, name, p = DEFAULT_PROGRESSION) {
    if (!player.hive.structures[name]) throw new Error(`${name} isn't on the Hive`)
    const out = clonePlayer(player)
    delete out.hive.structures[name]
    return assertHive(out, p)
}

// ── Hive Level and arenas ───────────────────────────────────────────────────

// The sum of the levels of every structure placed on the Hive.
export function hiveLevel(player) {
    return Object.keys(player.hive.structures).reduce((sum, name) => sum + (player.structures[name]?.level ?? 0), 0)
}

// The next arena up and the Hive Level that reaches it; null at the top.
export function nextArena(player, p = DEFAULT_PROGRESSION) {
    const arena = player.arena + 1
    return p.arenas[arena] ? { arena, hiveLevel: p.arenas[arena].hiveLevelToAscendInto } : null
}

// What the player owns but hasn't put on the Hive, and could put there now:
// every unplaced lot (owned lots never outnumber the Hive's cells, so one
// always has a hex), and the unplaced structures while the arena leaves a
// free slot. Returns { lots: { type: n }, structures: [name], freeSlots,
// count }, `count` being how many placements that is (structures only up to
// the free slots). Both in the config's order.
export function toPlace(player, p = DEFAULT_PROGRESSION) {
    const lots = {}
    for (const type of Object.keys(p.lotTypes)) {
        const n = unplacedLots(player, type)
        if (n > 0) lots[type] = n
    }
    const cap = Math.min(p.arenas[player.arena].maxStructures, p.inputs.maxStructuresHardCap)
    const freeSlots = Math.max(0, cap - Object.keys(player.hive.structures).length)
    const unplaced = structureNames(p).filter(n => ownsStructure(player, n) && !player.hive.structures[n])
    const structures = freeSlots > 0 ? unplaced : []
    const count = Object.values(lots).reduce((a, n) => a + n, 0) + Math.min(structures.length, freeSlots)
    return { lots, structures, freeSlots, count }
}

// The arena the player's Hive Level qualifies for: never lower than the one
// they're in (arenas never go down), and it may skip several at once.
export function qualifyingArena(player, p = DEFAULT_PROGRESSION) {
    const level = hiveLevel(player)
    let arena = player.arena
    while (p.arenas[arena + 1] && level >= p.arenas[arena + 1].hiveLevelToAscendInto) arena++
    return arena
}

// ── Lot yield ───────────────────────────────────────────────────────────────

// Same-type lot networks (re-exported from the war's own yield rules).
export { lotNetworks }

// One round's lot yield for a Hive in a war — the war's own rule
// (subplotEconomy.lotYield) with this config's numbers.
//   hive:      { lots: { [cell]: type }, structures: [{ name, footprint }] }
//   destroyed: Set of destroyed cells
// Returns { total, byType } (whole numbers, floored per type).
export function hiveYield(hive, destroyed = new Set(), p = DEFAULT_PROGRESSION) {
    const structures = hive.structures.map(st => {
        const def = p.structures[st.name]
        if (!def) throw new Error(`Unknown structure "${st.name}"`)
        return { footprint: st.footprint, extractorFor: def.extractorFor, stopsLotProduction: def.stopsLotProduction }
    })
    return lotYield({ lots: hive.lots, structures, destroyed },
        { perLot: p.inputs.yieldPerLotPerRound, multiplier: p.inputs.extractorYieldMultiplier })
}

// The war-ready form of a player's Hive (what a war snapshots): lots plus
// each placed structure's footprint, type and level.
export function warHive(player, p = DEFAULT_PROGRESSION) {
    const fps = placedFootprints(player, p)
    return {
        lots: { ...player.hive.lots },
        structures: Object.keys(player.hive.structures).map(name => ({
            name, level: player.structures[name].level, footprint: fps[name], ...player.hive.structures[name],
        })),
    }
}
